import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, InputAdornment, MenuItem, Rating,
  Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LinkIcon from '@mui/icons-material/Link'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import { motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { CreateVendorRequest, Part, Vendor } from '@cmms/types'
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined'

import { primary } from '../../theme/palette'
import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { DateRangeFilterPills, useDateRangeFilter, WorkOrderTimelineList, type WorkOrderTimelineItem } from '../../components/WorkOrderTimeline'
import { useConfirm } from '../../components/ConfirmDialog'
import { useCopyLink } from '../../hooks/useCopyLink'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { usePermissions } from '../../hooks/usePermissions'
import { DocumentUploader } from '../../components/DocumentUploader'
import { SearchableSelect } from '../../components/SearchableSelect'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { CustomerScopeChip } from '../../components/CustomerScopeChip'
import { BulkActionBar } from '../../components/BulkActionBar'
import { useBulkSelection } from '../../hooks/useBulkSelection'
import { formatSiteName } from '../../lib/siteDisplayName'

export function VendorsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { vendorId: urlVendorId } = useParams<{ vendorId?: string }>()
  const confirmDlg = useConfirm()
  const copyLink = useCopyLink()

  const { can } = usePermissions()
  const [selectedId, setSelectedId] = useState<string | null>(urlVendorId ?? null)
  useEffect(() => { if (urlVendorId) setSelectedId(urlVendorId) }, [urlVendorId])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [formOpen, setFormOpen] = useState<{ mode: 'create' } | { mode: 'edit'; vendor: Vendor } | null>(null)

  const vendorsQ = useQuery({
    queryKey: ['vendors', { q: search.trim() || undefined, is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined }],
    queryFn: () => api.vendors.list({
      q: search.trim() || undefined,
      is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
    }),
  })
  const vendors: Vendor[] = Array.isArray(vendorsQ.data) ? vendorsQ.data : []

  if (!selectedId && vendors.length > 0) {
    const first = vendors[0]!.id
    setSelectedId(first)
    navigate(`/vendors/${first}`, { replace: true })
  }
  function handleSelectVendor(id: string) {
    setSelectedId(id)
    navigate(`/vendors/${id}`, { replace: true })
  }
  const selected = selectedId ? vendors.find((v) => v.id === selectedId) ?? null : null

  const deleteM = useMutation({
    mutationFn: (id: string) => api.vendors.remove(id),
    onSuccess: () => { toast.success('Vendor deleted'); qc.invalidateQueries({ queryKey: ['vendors'] }); setSelectedId(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const bulk = useBulkSelection()
  const bulkDeleteM = useMutation({
    mutationFn: (ids: string[]) => api.vendors.bulkRemove(ids),
    onSuccess: (res) => {
      const okCount = res.deleted.length
      const skipCount = res.skipped.length
      if (okCount > 0 && skipCount === 0) toast.success(`Deleted ${okCount} vendor${okCount === 1 ? '' : 's'}`)
      else if (okCount > 0 && skipCount > 0) toast.success(`Deleted ${okCount}, skipped ${skipCount}`)
      else toast.error('Nothing was deleted')
      qc.invalidateQueries({ queryKey: ['vendors'] })
      bulk.clear()
      if (selectedId && res.deleted.includes(selectedId)) setSelectedId(null)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Bulk delete failed'),
  })
  async function handleBulkDelete() {
    const ids = bulk.ids
    if (ids.length === 0) return
    const ok = await confirmDlg({
      title: `Delete ${ids.length} vendor${ids.length === 1 ? '' : 's'}?`,
      message: 'They will be soft-deleted and disappear from this list. You can re-import them anytime.',
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (ok) bulkDeleteM.mutate(ids)
  }

  const toolbar = (
    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
      <TextField placeholder="Search vendors" value={search} onChange={(e) => setSearch(e.target.value)} size="small"
        sx={{ width: { xs: '100%', md: 300 }, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper', fontSize: 13, borderRadius: 2, '& fieldset': { borderColor: 'divider' } } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment> }} />
      <Box sx={{ width: 1 }} />
      <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} size="small" variant="standard" disableUnderline
        renderValue={() => <Stack direction="row" alignItems="center" spacing={0.85} sx={{ px: 0.5 }}><Box sx={{ color: statusFilter !== 'all' ? 'primary.main' : 'text.secondary', display: 'inline-flex' }}><FiberManualRecordIcon sx={{ fontSize: 10 }} /></Box><Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: statusFilter !== 'all' ? 'text.primary' : 'text.secondary' }}>{statusFilter === 'all' ? 'Status' : statusFilter === 'active' ? 'Active' : 'Inactive'}</Box></Stack>}
        sx={{ height: 32, borderRadius: 2, border: '1px solid', borderColor: statusFilter !== 'all' ? 'primary.main' : 'divider', bgcolor: 'background.paper', '& .MuiSelect-select': { py: 0, pr: '28px !important', pl: 1.25 }, '& .MuiSelect-icon': { color: 'text.secondary', fontSize: 18, right: 6 } }}>
        <MenuItem value="all">All</MenuItem><MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
      </Select>
    </Stack>
  )

  return (
    <>
      <PageShell title="Vendors" toolbar={toolbar} scrollContent={false}
        actions={
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
            <Tooltip title={can('vendors:create') ? '' : "You don't have permission to add vendors"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => setFormOpen({ mode: 'create' })} disabled={!can('vendors:create')}
                sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, boxShadow: primary.glow }}>New vendor</Button></Box>
            </Tooltip>
          </motion.div>
        }>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: { xs: 3, md: 4 } }}>
          {vendorsQ.isLoading ? (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
          ) : (
            <>
              <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0, width: { xs: '100%', md: 360 }, flexShrink: 0, borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                  {can('vendors:delete') && vendors.length > 0 && (
                    <Checkbox size="small"
                      checked={bulk.allSelected(vendors.map((v) => v.id))}
                      indeterminate={bulk.someSelected(vendors.map((v) => v.id))}
                      onChange={(e) => bulk.replaceWith(e.target.checked ? vendors.map((v) => v.id) : [])}
                      sx={{ p: 0.5 }} />
                  )}
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>
                    {bulk.count > 0 ? `${bulk.count} selected` : `${vendors.length} vendor${vendors.length === 1 ? '' : 's'}`}
                  </Typography>
                </Stack>
                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1, scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
                  '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
                  '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
                  {vendors.map((v, i) => {
                    const sel = v.id === selectedId
                    const checked = bulk.isSelected(v.id)
                    return (
                      <motion.div key={v.id} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(0.4, i * 0.012), duration: 0.24 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5} onClick={() => handleSelectVendor(v.id)}
                          sx={{ position: 'relative', px: 1.5, py: 1.25, cursor: 'pointer', transition: 'background-color 120ms ease',
                            bgcolor: sel ? primary.selected : 'transparent',
                            '&:hover': { bgcolor: sel ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
                            '&::before': sel ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined }}>
                          {can('vendors:delete') && (
                            <Checkbox size="small" checked={checked}
                              onClick={(e) => { e.stopPropagation(); bulk.toggle(v.id) }}
                              onChange={() => {/* handled in onClick */}}
                              sx={{ p: 0.5 }} />
                          )}
                          <Box sx={{ width: 38, height: 38, borderRadius: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.05)', color: 'text.secondary', flexShrink: 0 }}>
                            <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: sel ? 600 : 500 }} noWrap>{v.name}</Typography>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.2 }}>
                              {v.category && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{v.category}</Typography>}
                              <CustomerScopeChip customerId={v.customer_id} />
                              {!v.is_active && <Chip label="Inactive" size="small" sx={{ height: 16, fontSize: 9.5, bgcolor: 'action.hover', color: 'text.secondary' }} />}
                            </Stack>
                          </Box>
                        </Stack>
                      </motion.div>
                    )
                  })}
                  {vendors.length === 0 && <Box sx={{ px: 3, py: 4, textAlign: 'center' }}><Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No vendors yet.</Typography></Box>}
                </Box>
                <BulkActionBar count={bulk.count} label="vendor"
                  deleting={bulkDeleteM.isPending}
                  onClear={bulk.clear} onDelete={handleBulkDelete} />
              </Box>

              {selected ? <VendorDetail vendor={selected}
                deleting={deleteM.isPending}
                onEdit={() => setFormOpen({ mode: 'edit', vendor: selected })}
                onDelete={async () => {
                  const ok = await confirmDlg({ title: `Delete ${selected.name}?`, message: 'This vendor will be permanently removed.', confirmLabel: 'Delete', variant: 'danger' })
                  if (ok) deleteM.mutate(selected.id)
                }}
                copyLink={copyLink} /> : (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Select a vendor</Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </PageShell>

      {formOpen && <VendorFormDialog mode={formOpen.mode} vendor={formOpen.mode === 'edit' ? formOpen.vendor : undefined} onClose={() => setFormOpen(null)} />}
    </>
  )
}

// ─── Rich vendor detail pane ────────────────────────────────────────────────

function VendorDetail({ vendor, onEdit, onDelete, copyLink, deleting }: {
  vendor: Vendor; onEdit: () => void; onDelete: () => void; copyLink: (path: string) => void; deleting?: boolean
}) {
  const navigate = useNavigate()
  const { formatDate, prefs } = useOrgSettings()
  const { can } = usePermissions()

  const assetsQ = useQuery({
    queryKey: ['assets', { vendor_id: vendor.id }],
    queryFn: () => api.assets.list({}),
    staleTime: 30_000,
  })
  const allAssets = Array.isArray(assetsQ.data) ? assetsQ.data : []
  const linkedAssets = allAssets.filter((a: any) => {
    const meta = (a.metadata ?? {}) as Record<string, any>
    return meta.vendor_id === vendor.id
  })

  const partsQ = useQuery({
    queryKey: ['parts', { vendor_id: vendor.id }],
    queryFn: () => api.parts.list({}),
    staleTime: 30_000,
  })
  const allParts: Part[] = Array.isArray(partsQ.data) ? partsQ.data : []
  const linkedParts = allParts.filter((p) => p.vendor_id === vendor.id)

  const contractDays = vendor.contract_start && vendor.contract_end
    ? Math.round((new Date(vendor.contract_end).getTime() - new Date(vendor.contract_start).getTime()) / (24 * 60 * 60 * 1000))
    : null
  const contractRemaining = vendor.contract_end
    ? Math.round((new Date(vendor.contract_end).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    : null
  const contractProgress = contractDays && contractRemaining != null
    ? Math.max(0, Math.min(100, ((contractDays - contractRemaining) / contractDays) * 100))
    : null
  const isExpired = contractRemaining != null && contractRemaining < 0

  return (
    <motion.div key={vendor.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.26 }}
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>

      {/* Header */}
      <Box sx={{ px: 4, pt: 3.5, pb: 2.5, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          <Box sx={{ width: 48, height: 48, borderRadius: 1.75, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.06)', color: 'text.secondary', flexShrink: 0 }}>
            <StorefrontOutlinedIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{vendor.name}</Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
              {vendor.category && <Chip label={vendor.category} size="small" sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: 'action.hover', color: 'text.secondary' }} />}
              <Chip label={vendor.is_active ? 'Active' : 'Inactive'} size="small"
                sx={{ height: 22, fontSize: 11.5, fontWeight: 600, bgcolor: 'action.hover', color: vendor.is_active ? '#059669' : '#94A3B8' }} />
              {vendor.rating != null && <Rating value={vendor.rating} readOnly size="small" sx={{ fontSize: 16 }} />}
            </Stack>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Tooltip title="Copy link" arrow>
              <IconButton size="small" onClick={() => copyLink(`/vendors/${vendor.id}`)} sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34 }}>
                <LinkIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
            <Tooltip title={can('vendors:update') ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={onEdit} disabled={!can('vendors:update')}
                sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider', height: 34 }}>Edit</Button></Box>
            </Tooltip>
            <Tooltip title={can('vendors:delete') ? 'Delete' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><IconButton size="small" onClick={onDelete} disabled={!can('vendors:delete') || deleting}
                sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34, '&:hover': { color: 'error.main' } }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton></Box></Tooltip>
          </Stack>
        </Stack>
      </Box>
      <Divider />

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3, scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>

        {/* ── Stat cards ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 3 }}>
          <StatCard icon={<FiberManualRecordIcon sx={{ fontSize: 10, color: vendor.is_active ? '#059669' : '#94A3B8' }} />}
            label="Status" value={vendor.is_active ? 'Active' : 'Inactive'} />
          <StatCard icon={<Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>{prefs.currencySymbol}</Typography>}
            label="Payment" value={vendor.payment_terms ?? 'Not set'} />
          <StatCard icon={<AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Contract"
            value={contractRemaining != null ? (isExpired ? 'Expired' : `${contractRemaining}d left`) : 'No contract'}
            tone={isExpired ? 'danger' : contractRemaining != null && contractRemaining < 30 ? 'warn' : 'neutral'} />
          <StatCard icon={<StorefrontOutlinedIcon sx={{ fontSize: 15 }} />}
            label="Category" value={vendor.category ?? 'Uncategorized'} />
        </Box>

        {/* ── Contract timeline bar ── */}
        {vendor.contract_start && vendor.contract_end && (
          <Box sx={{ mb: 3, px: 2.5, py: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider',
            bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Contract period</Typography>
              <Typography sx={{ fontSize: 12, color: isExpired ? '#DC2626' : 'text.secondary', fontWeight: 600 }}>
                {isExpired ? `Expired ${-contractRemaining!}d ago` : `${contractRemaining}d remaining`}
              </Typography>
            </Stack>
            <Box sx={{ height: 6, borderRadius: 99, bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)', overflow: 'hidden', mb: 1 }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${contractProgress ?? 0}%` }}
                transition={{ duration: 0.8, ease: [0.32, 0.72, 0, 1] }}
                style={{ height: '100%', borderRadius: 99, background: isExpired ? '#DC2626' : contractRemaining! < 30 ? '#D97706' : '#059669' }} />
            </Box>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {formatDate(vendor.contract_start)}
              </Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                {formatDate(vendor.contract_end)}
              </Typography>
            </Stack>
          </Box>
        )}

        {/* ── Two-column layout for contact + financial ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
          {/* Contact card */}
          <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Contact</Typography>
            </Box>
            <Stack spacing={1.5} sx={{ px: 2.5, py: 2 }}>
              {vendor.contact_name && <Kv icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />} label="Person" value={vendor.contact_name} tip="Primary contact person at this vendor" />}
              {vendor.email && <Kv icon={<EmailOutlinedIcon sx={{ fontSize: 15 }} />} label="Email" value={vendor.email} tip="Vendor's email — used for reorder requests" />}
              {vendor.phone && <Kv icon={<PhoneOutlinedIcon sx={{ fontSize: 15 }} />} label="Phone" value={vendor.phone} tip="Vendor's phone number" />}
              {vendor.website && <Kv icon={<LanguageOutlinedIcon sx={{ fontSize: 15 }} />} label="Web" value={vendor.website} tip="Vendor's website" />}
              {vendor.address && <Kv icon={<LocationOnOutlinedIcon sx={{ fontSize: 15 }} />} label="Address" value={vendor.address} tip="Vendor's physical address" />}
              {!vendor.contact_name && !vendor.email && !vendor.phone && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontStyle: 'italic' }}>No contact info added yet.</Typography>
              )}
            </Stack>
          </Box>

          {/* Financial card */}
          <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Financial & legal</Typography>
            </Box>
            <Stack spacing={1.5} sx={{ px: 2.5, py: 2 }}>
              {vendor.tax_id && <Kv icon={<DescriptionOutlinedIcon sx={{ fontSize: 15 }} />} label="Tax ID" value={vendor.tax_id} tip="Vendor's tax or business registration number" />}
              {vendor.account_number && <Kv icon={<Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>{prefs.currencySymbol}</Typography>} label="Account" value={vendor.account_number} tip="Your account number with this vendor" />}
              {vendor.payment_terms && <Kv icon={<AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />} label="Terms" value={vendor.payment_terms} tip="Payment terms agreed with this vendor" />}
              {!vendor.tax_id && !vendor.account_number && !vendor.payment_terms && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontStyle: 'italic' }}>No financial info added yet.</Typography>
              )}
            </Stack>
          </Box>
        </Box>

        {/* ── Service agreements (real uploads) ── */}
        <VendorDocumentsSection vendorId={vendor.id} />

        {/* ── Linked assets & parts side by side ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 3 }}>
          {/* Assets */}
          <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minHeight: 0 }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Inventory2OutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Assets</Typography>
                </Stack>
                <Chip label={linkedAssets.length} size="small"
                  sx={{ height: 18, minWidth: 24, fontSize: 10, fontWeight: 700,
                    bgcolor: linkedAssets.length > 0 ? (t) => `${t.palette.primary.main}18` : 'action.hover',
                    color: linkedAssets.length > 0 ? 'primary.main' : 'text.secondary' }} />
              </Stack>
            </Box>
            {linkedAssets.length > 0 ? (
              <Stack spacing={0} sx={{ maxHeight: 240, overflowY: 'auto', scrollbarWidth: 'thin' }}>
                {linkedAssets.map((a: any) => (
                  <Stack key={a.id} direction="row" alignItems="center" spacing={1.25}
                    onClick={() => navigate(`/assets/${a.id}`)}
                    sx={{
                      px: 2, py: 1.25, cursor: 'pointer', borderBottom: '1px solid', borderColor: 'divider',
                      transition: 'background-color 0.15s',
                      '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)' },
                      '&:last-child': { borderBottom: 'none' },
                    }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      bgcolor: a.category_color ? `${a.category_color}18` : 'action.hover',
                      color: a.category_color ?? 'text.secondary',
                    }}>
                      <Inventory2OutlinedIcon sx={{ fontSize: 14 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 500 }} noWrap>{a.name}</Typography>
                      <Typography sx={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>{a.asset_code}</Typography>
                    </Box>
                    <OpenInNewOutlinedIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Box sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>No assets linked yet.</Typography>
              </Box>
            )}
          </Box>

          {/* Parts */}
          <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden', minHeight: 0 }}>
            <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <WarehouseOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Parts</Typography>
                </Stack>
                <Chip label={linkedParts.length} size="small"
                  sx={{ height: 18, minWidth: 24, fontSize: 10, fontWeight: 700,
                    bgcolor: linkedParts.length > 0 ? (t) => `${t.palette.primary.main}18` : 'action.hover',
                    color: linkedParts.length > 0 ? 'primary.main' : 'text.secondary' }} />
              </Stack>
            </Box>
            {linkedParts.length > 0 ? (
              <Stack spacing={0} sx={{ maxHeight: 240, overflowY: 'auto', scrollbarWidth: 'thin' }}>
                {linkedParts.map((p) => {
                  const stColor = p.status === 'out_of_stock' ? '#DC2626' : p.status === 'low_stock' ? '#D97706' : '#059669'
                  return (
                    <Stack key={p.id} direction="row" alignItems="center" spacing={1.25}
                      onClick={() => navigate(`/inventory/${p.id}`)}
                      sx={{
                        px: 2, py: 1.25, cursor: 'pointer', borderBottom: '1px solid', borderColor: 'divider',
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)' },
                        '&:last-child': { borderBottom: 'none' },
                      }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(15,23,42,0.04)' : 'rgba(255,255,255,0.05)',
                        color: 'text.secondary', position: 'relative',
                      }}>
                        <WarehouseOutlinedIcon sx={{ fontSize: 14 }} />
                        <Box sx={{ position: 'absolute', right: -2, bottom: -2, width: 7, height: 7, borderRadius: 99, bgcolor: stColor, border: '1.5px solid', borderColor: 'background.paper' }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 500 }} noWrap>{p.name}</Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography sx={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>{p.sku ?? p.part_code}</Typography>
                          <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>·</Typography>
                          <Typography sx={{ fontSize: 10, color: stColor, fontWeight: 600 }}>{p.quantity} qty</Typography>
                        </Stack>
                      </Box>
                      <OpenInNewOutlinedIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                    </Stack>
                  )
                })}
              </Stack>
            ) : (
              <Box sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>No parts from this vendor yet.</Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* ── Linked work orders / tasks ── */}
        <VendorWorkOrdersSection vendorId={vendor.id} />

        {/* ── Quick links ── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 3 }}>
          <QuickLink icon={<Inventory2OutlinedIcon sx={{ fontSize: 18 }} />} label="View assets" sub={`${linkedAssets.length} linked`} href="/assets" onClick={() => navigate('/assets')} />
          <QuickLink icon={<WarehouseOutlinedIcon sx={{ fontSize: 18 }} />} label="Inventory" sub={`${linkedParts.length} parts`} href="/inventory" onClick={() => navigate('/inventory')} />
          <QuickLink icon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />} label="Work orders" sub="From this vendor" href="/work-orders" onClick={() => navigate('/work-orders')} />
        </Box>

        {/* ── Scope of work ── */}
        {vendor.notes && (
          <Box sx={{ mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
              bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Scope of work</Typography>
            </Box>
            <Box sx={{ px: 2.5, py: 2 }}>
              <Typography sx={{ fontSize: 13.5, lineHeight: 1.65, color: 'text.secondary' }}>{vendor.notes}</Typography>
            </Box>
          </Box>
        )}

        {/* ── Footer metadata ── */}
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', textAlign: 'center' }}>
          Created {formatDate(vendor.created_at)}
        </Typography>
      </Box>
    </motion.div>
  )
}

function VendorWorkOrdersSection({ vendorId }: { vendorId: string }) {
  const navigate = useNavigate()
  const { formatDate } = useOrgSettings()
  const woQ = useQuery({
    queryKey: ['vendor-work-orders', vendorId],
    queryFn: () => api.workOrders.list({ vendor_id: vendorId }),
    staleTime: 30_000,
  })
  const workOrders = Array.isArray(woQ.data) ? woQ.data : []
  const sorted = [...workOrders].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
  const { range, setRange, filtered } = useDateRangeFilter(sorted, (wo) => wo.created_at)

  const items: WorkOrderTimelineItem[] = filtered.map((wo) => ({
    id: wo.id,
    title: wo.title,
    code: wo.wo_code,
    status: wo.status,
    priority: wo.priority,
    isTask: wo.kind === 'task',
    assignee: wo.assigned_to_user_name ?? wo.assigned_to_team_name ?? null,
    createdAt: wo.created_at,
    finishedAt: wo.closed_at ?? wo.resolved_at ?? null,
    onClick: () => navigate(`/work-orders/${wo.id}`),
  }))

  return (
    <Box sx={{ mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <AssignmentOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>Work orders</Typography>
        </Stack>
      </Box>
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <DateRangeFilterPills range={range} onChange={setRange} />
        <WorkOrderTimelineList
          items={items}
          formatDate={formatDate}
          loading={woQ.isLoading}
          emptyMessage={sorted.length === 0 ? 'No work orders from this vendor yet.' : 'No work orders in this date range.'}
        />
      </Box>
    </Box>
  )
}

function StatCard({ icon, label, value, tone = 'neutral' }: { icon: React.ReactNode; label: string; value: string; tone?: 'neutral' | 'warn' | 'danger' }) {
  const toneColor = tone === 'danger' ? '#DC2626' : tone === 'warn' ? '#D97706' : 'text.primary'
  return (
    <Box sx={{ px: 2, py: 1.75, borderRadius: 1.75, border: '1px solid', borderColor: 'divider',
      bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: 'text.secondary', mb: 0.75 }}>
        {icon}
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>{label}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 15, fontWeight: 700, letterSpacing: '-0.01em', color: toneColor }}>{value}</Typography>
    </Box>
  )
}

function QuickLink({ icon, label, sub, onClick }: { icon: React.ReactNode; label: string; sub: string; href?: string; onClick?: () => void }) {
  return (
    <Box onClick={onClick} sx={{ px: 2, py: 2, borderRadius: 1.75, border: '1px solid', borderColor: 'divider', cursor: 'pointer',
      transition: 'border-color 120ms ease', '&:hover': { borderColor: 'primary.main' } }}>
      <Box sx={{ color: 'text.secondary', mb: 1 }}>{icon}</Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
      <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{sub}</Typography>
    </Box>
  )
}

// ─── Vendor documents section ────────────────────────────────────────────────

function VendorDocumentsSection({ vendorId }: { vendorId: string }) {
  const qc = useQueryClient()
  const { can } = usePermissions()
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [uploading, setUploading] = useState(false)

  const docsQ = useQuery({ queryKey: ['vendor-documents', vendorId], queryFn: () => api.vendors.listDocuments(vendorId) })
  const documents = Array.isArray(docsQ.data) ? docsQ.data : []

  async function handleUpload(file: File) {
    setUploading(true)
    setUploadProgress(0)
    try {
      await api.vendors.uploadDocument(vendorId, file, (pct) => setUploadProgress(pct))
      qc.invalidateQueries({ queryKey: ['vendor-documents', vendorId] })
      toast.success('Document uploaded')
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Upload failed')
    } finally {
      setUploading(false)
      setUploadProgress(null)
    }
  }

  async function handleDelete(docId: string) {
    try {
      await api.vendors.deleteDocument(vendorId, docId)
      qc.invalidateQueries({ queryKey: ['vendor-documents', vendorId] })
      toast.success('Document removed')
    } catch {
      toast.error('Failed to remove')
    }
  }

  return (
    <Box sx={{ mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }}>
            Service agreements & documents
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{documents.length}/5</Typography>
        </Stack>
      </Box>
      <Box sx={{ px: 2.5, py: 2 }}>
        <DocumentUploader
          documents={documents}
          maxFiles={5}
          uploading={uploading}
          uploadProgress={uploadProgress}
          onUpload={handleUpload}
          onDelete={handleDelete}
          canEdit={can('vendors:update')}
          onView={(doc) => api.vendors.viewDocument(vendorId, doc.id)}
        />
      </Box>
    </Box>
  )
}

// ─── Vendor form dialog ─────────────────────────────────────────────────────

function VendorFormDialog({ mode, vendor, onClose }: { mode: 'create' | 'edit'; vendor?: Vendor; onClose: () => void }) {
  const qc = useQueryClient()

  // Fetch asset categories for the category selector (linked data)
  const categoriesQ = useQuery({ queryKey: ['asset-categories'], queryFn: () => api.assets.listCategories(), staleTime: 5 * 60 * 1000 })
  const assetCategories = Array.isArray(categoriesQ.data) ? categoriesQ.data : []

  const [name, setName] = useState(vendor?.name ?? '')
  const [contactName, setContactName] = useState(vendor?.contact_name ?? '')
  const [email, setEmail] = useState(vendor?.email ?? '')
  const [phone, setPhone] = useState(vendor?.phone ?? '')
  const [website, setWebsite] = useState(vendor?.website ?? '')
  const [address, setAddress] = useState(vendor?.address ?? '')
  const [category, setCategory] = useState(vendor?.category ?? '')
  const [rating, setRating] = useState<number | null>(vendor?.rating ?? null)
  const [taxId, setTaxId] = useState(vendor?.tax_id ?? '')
  const [paymentTerms, setPaymentTerms] = useState(vendor?.payment_terms ?? '')
  const [contractStart, setContractStart] = useState(vendor?.contract_start?.slice(0, 10) ?? '')
  const [contractEnd, setContractEnd] = useState(vendor?.contract_end?.slice(0, 10) ?? '')
  const [accountNumber, setAccountNumber] = useState(vendor?.account_number ?? '')
  const [notes, setNotes] = useState(vendor?.notes ?? '')
  const [siteId, setSiteId] = useState(vendor?.site_id ?? '')
  // null = shared across all customers (catalog default).
  const [customerId, setCustomerId] = useState<string | null>(vendor?.customer_id ?? null)

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000 })
  const sites = Array.isArray(sitesQ.data) ? sitesQ.data : []

  const m = useMutation({
    mutationFn: () => {
      // On edit, a field the user just cleared must be sent as an
      // explicit `null` — omitting it (`undefined`) means "leave
      // unchanged" to the backend's sparse update, so the old value
      // would silently survive a "successful" save.
      const empty = mode === 'edit' ? null : undefined
      const body = {
        name: name.trim(), contact_name: contactName.trim() || empty,
        email: email.trim() || empty, phone: phone.trim() || empty,
        website: website.trim() || empty, address: address.trim() || empty,
        category: category || empty, rating: rating ?? empty,
        tax_id: taxId.trim() || empty, payment_terms: paymentTerms || empty,
        contract_start: contractStart || empty, contract_end: contractEnd || empty,
        account_number: accountNumber.trim() || empty,
        notes: notes.trim() || empty,
        site_id: siteId || null,
        // Always send customer_id — null is meaningful here ("share
        // org-wide") and we need it to make the field switchable.
        customer_id: customerId,
      }
      return mode === 'create' ? api.vendors.create(body as unknown as CreateVendorRequest) : api.vendors.update(vendor!.id, body)
    },
    onSuccess: () => { toast.success(mode === 'create' ? 'Vendor created' : 'Vendor updated'); qc.invalidateQueries({ queryKey: ['vendors'] }); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{mode === 'create' ? 'New vendor' : `Edit ${vendor?.name}`}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 1 }}>
          {/* Left column */}
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <SLabel>General</SLabel>
            <TextField label="Vendor name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus
              helperText="The company or supplier name" />
            <Tooltip title="Link this vendor to an asset category for easier tracking" arrow placement="top-start">
              <Box>
                <SearchableSelect
                  value={category}
                  onChange={setCategory}
                  options={assetCategories}
                  getValue={(c) => c.name}
                  getLabel={(c) => c.name}
                  placeholder="Select category"
                  renderOption={(c) => (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {c.color && <Box component="span" sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: c.color, display: 'inline-block' }} />}
                      <Typography sx={{ fontSize: 13 }}>{c.name}</Typography>
                    </Stack>
                  )}
                />
              </Box></Tooltip>
            <Tooltip title="Rate this vendor from 1 to 5 stars based on your experience" arrow placement="top-start">
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 0.75 }}>Rating</Typography>
                <Rating value={rating} onChange={(_, v) => setRating(v)} />
              </Box>
            </Tooltip>

            <Divider />
            <SLabel>Contact</SLabel>
            <TextField label="Contact person" value={contactName} onChange={(e) => setContactName(e.target.value)} size="small" fullWidth
              placeholder="e.g. John Smith" helperText="Your main point of contact at this vendor" />
            <Stack direction="row" spacing={2}>
              <Tooltip title="Used for reorder requests and communication" arrow placement="top-start">
                <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} size="small" fullWidth placeholder="vendor@example.com" />
              </Tooltip>
              <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} size="small" fullWidth placeholder="+1 555-123-4567" />
            </Stack>
            <TextField label="Website" value={website} onChange={(e) => setWebsite(e.target.value)} size="small" fullWidth placeholder="https://www.example.com" />
            <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)} size="small" fullWidth placeholder="123 Main St, City, State" />
          </Stack>

          {/* Right column */}
          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <SLabel>Customer scope</SLabel>
            <CustomerScopePicker
              mode="catalog"
              value={customerId}
              onChange={setCustomerId}
              helperText="Leave as All to share this vendor across every customer. Pick one to make it visible only to that customer."
            />
            <SLabel>Site access</SLabel>
            <Tooltip title="Restrict this vendor to a specific site, or leave as 'All sites' for everyone" arrow placement="top-start">
              <Box>
                <SearchableSelect
                  value={siteId}
                  onChange={setSiteId}
                  options={sites}
                  getValue={(s: any) => s.id}
                  getLabel={formatSiteName}
                  placeholder="All sites (no restriction)"
                />
              </Box>
            </Tooltip>

            <Divider />
            <SLabel>Financial & legal</SLabel>
            <Stack direction="row" spacing={2}>
              <Tooltip title="Vendor's tax or business registration number (e.g. GST, VAT, EIN)" arrow placement="top-start">
                <TextField label="Tax / Registration ID" value={taxId} onChange={(e) => setTaxId(e.target.value)} size="small" fullWidth />
              </Tooltip>
              <Tooltip title="Your account or customer number with this vendor" arrow placement="top-start">
                <TextField label="Your account number" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} size="small" fullWidth />
              </Tooltip>
            </Stack>
            <Tooltip title="How long you have to pay invoices from this vendor" arrow placement="top-start">
              <Box><Select value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} displayEmpty size="small" fullWidth>
              <MenuItem value="">Payment terms</MenuItem>
              <MenuItem value="Due on receipt">Due on receipt</MenuItem>
              <MenuItem value="Net 15">Net 15</MenuItem>
              <MenuItem value="Net 30">Net 30</MenuItem>
              <MenuItem value="Net 45">Net 45</MenuItem>
              <MenuItem value="Net 60">Net 60</MenuItem>
              <MenuItem value="Net 90">Net 90</MenuItem>
            </Select></Box></Tooltip>

            <Divider />
            <SLabel>Contract</SLabel>
            <Stack direction="row" spacing={2}>
              <Tooltip title="When the contract with this vendor started" arrow placement="top-start">
                <TextField label="Contract start" type="date" value={contractStart} onChange={(e) => setContractStart(e.target.value)}
                  size="small" fullWidth InputLabelProps={{ shrink: true }} />
              </Tooltip>
              <Tooltip title="When the contract expires — you'll see a timeline on the vendor detail" arrow placement="top-start">
                <TextField label="Contract end" type="date" value={contractEnd} onChange={(e) => setContractEnd(e.target.value)}
                  size="small" fullWidth InputLabelProps={{ shrink: true }} />
              </Tooltip>
            </Stack>

            {/* Service agreement upload */}
            {mode === 'edit' && vendor && (
              <>
                <Divider />
                <SLabel>Service agreements</SLabel>
                <VendorDocumentsInForm vendorId={vendor.id} />
              </>
            )}
            {mode === 'create' && (
              <>
                <Divider />
                <SLabel>Service agreements</SLabel>
                <Typography sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic' }}>
                  Save the vendor first, then upload documents from the detail view.
                </Typography>
              </>
            )}

            <Divider />
            <SLabel>Scope of work</SLabel>
            <TextField label="Scope of work" value={notes} onChange={(e) => setNotes(e.target.value)} size="small" fullWidth multiline minRows={3}
              placeholder="Describe what this vendor provides — services, products, coverage area..." />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create vendor' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Sec({ title, children }: { title: string; children: React.ReactNode }) {
  return <Box sx={{ py: 2.5 }}><Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.25 }}>{title}</Typography>{children}</Box>
}
function VendorDocumentsInForm({ vendorId }: { vendorId: string }) {
  const qc = useQueryClient()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)

  const docsQ = useQuery({ queryKey: ['vendor-documents', vendorId], queryFn: () => api.vendors.listDocuments(vendorId) })
  const docs = Array.isArray(docsQ.data) ? docsQ.data : []

  return (
    <DocumentUploader documents={docs} maxFiles={5} uploading={uploading} uploadProgress={progress} canEdit
      onView={(doc) => api.vendors.viewDocument(vendorId, doc.id)}
      onUpload={async (file) => {
        setUploading(true); setProgress(0)
        try { await api.vendors.uploadDocument(vendorId, file, (p) => setProgress(p)); qc.invalidateQueries({ queryKey: ['vendor-documents', vendorId] }); toast.success('Uploaded') }
        catch { toast.error('Upload failed') }
        finally { setUploading(false); setProgress(null) }
      }}
      onDelete={async (docId) => {
        try { await api.vendors.deleteDocument(vendorId, docId); qc.invalidateQueries({ queryKey: ['vendor-documents', vendorId] }); toast.success('Removed') }
        catch { toast.error('Failed') }
      }}
    />
  )
}

function SLabel({ children }: { children: React.ReactNode }) {
  return <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>{children}</Typography>
}
function Kv({ icon, label, value, tip }: { icon?: React.ReactNode; label: string; value: string; tip?: string }) {
  const row = (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={tip ? { cursor: 'help' } : undefined}>
      {icon && <Box sx={{ color: 'text.secondary', display: 'inline-flex', flexShrink: 0 }}>{icon}</Box>}
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', minWidth: 80 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{value}</Typography>
    </Stack>
  )
  return tip ? <Tooltip title={tip} arrow placement="top-start"><Box>{row}</Box></Tooltip> : row
}
